import { useMemo } from 'react';
import {
  startOfWeek,
  endOfWeek,
  addWeeks,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  startOfDay,
} from 'date-fns';
import type { Task } from '@/services/task.service';
import {
  PlannerPeriod,
  PlannerTask,
  DayCapacity,
  calculateTaskHealth,
  calculateDayCapacities,
  assignTaskRows,
  getTaskPosition,
  getEffectiveEndDateForTask,
} from './plannerUtils';

interface UsePlannerDataProps {
  tasks: Task[];
  workingDays?: string[];
  dailyHours?: number;
  period: PlannerPeriod;
  baseDate: Date;
}

interface UsePlannerDataReturn {
  periodDays: Date[];
  plannerTasks: PlannerTask[];
  dayCapacities: DayCapacity[];
  periodLabel: string;
}

export const usePlannerData = ({
  tasks,
  workingDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
  dailyHours = 8,
  period,
  baseDate,
}: UsePlannerDataProps): UsePlannerDataReturn => {
  const periodDays = useMemo(() => {
    let start: Date;
    let end: Date;

    switch (period) {
      case 'week':
        start = startOfWeek(baseDate, { weekStartsOn: 1 });
        end = endOfWeek(baseDate, { weekStartsOn: 1 });
        break;
      case '2-weeks':
        start = startOfWeek(baseDate, { weekStartsOn: 1 });
        end = endOfWeek(addWeeks(baseDate, 1), { weekStartsOn: 1 });
        break;
      case 'month':
        start = startOfMonth(baseDate);
        end = endOfMonth(baseDate);
        break;
      default:
        start = startOfWeek(baseDate, { weekStartsOn: 1 });
        end = endOfWeek(baseDate, { weekStartsOn: 1 });
    }

    return eachDayOfInterval({ start, end });
  }, [period, baseDate]);

  const periodLabel = useMemo(() => {
    if (periodDays.length === 0) return '';

    const start = periodDays[0];
    const end = periodDays[periodDays.length - 1];

    const formatOptions: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
    const startStr = start.toLocaleDateString('en-US', formatOptions);
    const endStr = end.toLocaleDateString('en-US', { ...formatOptions, year: 'numeric' });

    return `${startStr} - ${endStr}`;
  }, [periodDays]);

  const filteredTasks = useMemo(() => {
    if (!periodDays.length) return [];

    const periodStart = startOfDay(periodDays[0]);
    const periodEnd = startOfDay(periodDays[periodDays.length - 1]);

    return tasks.filter(task => {
      if (!task.start_date || !task.end_date) return false;

      const taskStart = startOfDay(new Date(task.start_date));

      // Calculate effective end date (extended for overdue tasks)
      const effectiveEnd = getEffectiveEndDateForTask(task, workingDays, dailyHours);

      // Task overlaps with period (using effective end for overdue tasks)
      return taskStart <= periodEnd && effectiveEnd >= periodStart;
    });
  }, [tasks, periodDays, workingDays, dailyHours]);

  const plannerTasks = useMemo(() => {
    if (!filteredTasks.length || !periodDays.length) return [];

    const taskRows = assignTaskRows(filteredTasks, periodDays, workingDays, dailyHours);

    return filteredTasks.map(task => {
      const health = calculateTaskHealth(task, tasks, workingDays, dailyHours);
      const position = getTaskPosition(task, periodDays, workingDays, dailyHours);
      const row = taskRows.get(task.id) || 0;

      return {
        ...task,
        health,
        row,
        ...position,
      };
    });
  }, [filteredTasks, tasks, periodDays, workingDays, dailyHours]);

  const dayCapacities = useMemo(() => {
    return calculateDayCapacities(periodDays, filteredTasks, workingDays, dailyHours);
  }, [periodDays, filteredTasks, workingDays, dailyHours]);

  return {
    periodDays,
    plannerTasks,
    dayCapacities,
    periodLabel,
  };
};
